import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BellRing, Bot, Gem, MessageCircle, Wrench } from 'lucide-react';
import logoMark from '../img/logo-branco.png';
import ScreensShowcase from './landing/ScreensShowcase';
import PlansSection from './landing/PlansSection';
import {
  ASK_PLACEHOLDER,
  FEATURE_GROUPS,
  FLOW_NODES,
  FONTS_HREF,
  HERO_PILLS,
  HIGHLIGHTS,
  ROTATING_WORDS,
  WHATSAPP_CONTACT_URL,
} from './landing/content';
import {
  prefersReducedMotion,
  useCardSpotlight,
  useCountUp,
  useInViewOnce,
  useRevealOnScroll,
  useTyper,
} from './landing/hooks';
import { LEGAL_LINKS_ENABLED } from './legal/legalLinks';
import './landing/landing.css';

// Landing page institucional — peça de marketing pública, FORA do shell do ERP.
// Ref: `ui-ux/landing-flow-vendas-v2.html`. Usa um sistema visual PRÓPRIO (indigo/violeta
// em gradiente, Sora + Inter, radius 18px/999px) que é diferente dos tokens do
// DESIGN-SYSTEM.md §2 de propósito; por isso os estilos ficam isolados em
// `landing/landing.css` (escopados em `.fvl`) e NÃO usam `theme.palette` (a landing
// também não segue o modo escuro do app). Não usar este visual em telas internas.

const MAIN_GROUPS = FEATURE_GROUPS.slice(0, -1);
const SPOT_GROUP = FEATURE_GROUPS[FEATURE_GROUPS.length - 1];
const TOTAL_FEATURES = FEATURE_GROUPS.reduce((total, group) => total + group.items.length, 0);
const MARQUEE_ITEMS = FEATURE_GROUPS.flatMap((group) => group.items);

// Rola até a âncora respeitando `prefers-reduced-motion`; o `href` continua no
// link para funcionar sem JS/abrir em nova aba.
function scrollToHash(hash) {
  const el = document.getElementById(hash.replace('#', ''));
  if (!el) return false;
  el.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
  return true;
}

function Brand({ onClick }) {
  return (
    <a href="#topo" className="brand" onClick={onClick}>
      <span className="brand-mark"><img className="logo-img" src={logoMark} alt="" style={{ width: 20, height: 20 }} /></span>
      Flow Vendas
    </a>
  );
}

function Stat({ to, suffix, label, play }) {
  const value = useCountUp(to, { play, idle: 0 });
  return (
    <div className="stat">
      <div className="n"><span>{value}</span>{suffix}</div>
      <div className="l">{label}</div>
    </div>
  );
}

export default function Landing() {
  const location = useLocation();
  const rootRef = useRef(null);
  const progressRef = useRef(null);
  const statsRef = useRef(null);
  const devicesRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);
  const [statsPlay, setStatsPlay] = useState(false);

  // Fontes próprias da landing (Sora + Inter), carregadas só enquanto ela está
  // montada — o resto do app segue com a fonte do tema.
  useEffect(() => {
    const links = [];
    const add = (attrs) => {
      const link = document.createElement('link');
      Object.entries(attrs).forEach(([k, v]) => link.setAttribute(k, v));
      document.head.appendChild(link);
      links.push(link);
    };
    add({ rel: 'preconnect', href: 'https://fonts.googleapis.com' });
    add({ rel: 'preconnect', href: 'https://fonts.gstatic.com', crossorigin: '' });
    add({ rel: 'stylesheet', href: FONTS_HREF });
    const previousTitle = document.title;
    document.title = 'Flow Vendas — ERP para lojas, oficinas e assistencia técnica';
    return () => {
      links.forEach((link) => link.remove());
      document.title = previousTitle;
    };
  }, []);

  // Nav com sombra ao rolar + barra de progresso de scroll (sem re-render por frame).
  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 8);
      const h = document.documentElement;
      const max = h.scrollHeight - h.clientHeight;
      if (progressRef.current) progressRef.current.style.width = `${max > 0 ? (window.scrollY / max) * 100 : 0}%`;
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Acesso direto a `/landing#contato`: o navegador tenta rolar antes do React
  // montar a seção, então repete a rolagem depois da renderização.
  useEffect(() => {
    if (location.hash) scrollToHash(location.hash);
  }, [location.hash]);

  // Contadores da faixa roxa: disparam ao entrar na viewport.
  useEffect(() => {
    const el = statsRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setStatsPlay(true);
      return undefined;
    }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setStatsPlay(true);
        io.disconnect();
      }
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useRevealOnScroll(rootRef);
  useCardSpotlight(rootRef);

  const devicesSeen = useInViewOnce(devicesRef, 0.3);
  const rotator = useTyper(ROTATING_WORDS);
  const ask = useTyper(ASK_PLACEHOLDER, { hold: 99999999, speed: 45, enabled: devicesSeen });

  const handleAnchor = (event) => {
    const hash = event.currentTarget.getAttribute('href');
    if (hash && hash.startsWith('#') && scrollToHash(hash)) {
      event.preventDefault();
      window.history.replaceState(null, '', `${location.pathname}${hash === '#topo' ? '' : hash}`);
    }
  };

  return (
    <div className="fvl" id="topo" ref={rootRef} style={{ minHeight: '100vh' }}>
      <div id="progress" ref={progressRef} />

      {/* ---------- Nav ---------- */}
      <nav className={`top${scrolled ? ' scrolled' : ''}`} aria-label="Navegação principal">
        <div className="wrap">
          <Brand onClick={handleAnchor} />
          <div className="nav-links">
            <a href="#funcionalidades" onClick={handleAnchor}>Funcionalidades</a>
            <a href="#planos" onClick={handleAnchor}>Planos</a>
          </div>
          <div className="nav-actions">
            <Link to="/login" className="link">Entrar</Link>
            <Link to="/login?modo=cadastro" className="btn btn-primary btn-sm">Testar grátis</Link>
          </div>
        </div>
      </nav>

      {/* ---------- Hero ---------- */}
      <header className="hero">
        <div className="aurora"><span /><span /><span /></div>
        <div className="grid-bg" />
        <div className="wrap">
          <div>
            <span className="badge reveal"><span className="dot" />ERP 100% na nuvem, sem instalação</span>
            <h1 className="reveal" style={{ '--d': '.08s' }}>
              Sua operação inteira, em <span className="grad-text">um único fluxo</span>.
              <span className="rotator grad-text" aria-live="off">{rotator}</span>
            </h1>
            <p className="lead reveal" style={{ '--d': '.16s' }}>
              O Flow Vendas é um ERP 100% na nuvem, sem necessidade de instalação, desenvolvido para simplificar e
              otimizar a gestão de vendas, controle de ordens de serviço e relacionamento com clientes — de forma
              integrada, de qualquer lugar e a qualquer momento, direto pelo navegador.
            </p>
            <div className="ctas reveal" style={{ '--d': '.24s' }}>
              <Link to="/login?modo=cadastro" className="btn btn-primary">Testar grátis por 14 dias</Link>
              <a href="#funcionalidades" className="btn btn-ghost" onClick={handleAnchor}>Ver funcionalidades</a>
            </div>
            <div className="pills reveal" style={{ '--d': '.32s' }}>
              {HERO_PILLS.map((pill) => <span className="pill" key={pill}>{pill}</span>)}
            </div>
          </div>

          {/* Diagrama de fluxo — decorativo */}
          <div className="orbit reveal right" aria-hidden="true" style={{ '--d': '.2s' }}>
            <svg className="ring" viewBox="0 0 300 300">
              <circle className="ring-dash" cx="150" cy="150" r="118" fill="none" stroke="#c9cbe0" strokeWidth="2" strokeDasharray="1 10" strokeLinecap="round" />
              <g className="flow-lines">
                <line className="flow-line" x1="150" y1="150" x2="150" y2="18" />
                <line className="flow-line" x1="150" y1="150" x2="285" y2="102" />
                <line className="flow-line" x1="150" y1="150" x2="234" y2="234" />
                <line className="flow-line" x1="150" y1="150" x2="66" y2="234" />
                <line className="flow-line" x1="150" y1="150" x2="15" y2="102" />
              </g>
            </svg>
            {FLOW_NODES.map(({ key, label, color, Icon, top, left, delay }) => (
              <div className="node" key={key} style={{ top, left, ...(delay ? { animationDelay: delay } : {}) }}>
                <div className="ic" style={{ background: color }}><Icon className="i" /></div>
                <b>{label}</b>
              </div>
            ))}
            <div className="core"><img className="logo-img" src={logoMark} alt="" style={{ width: 54, height: 54 }} /></div>
            <div className="chip c1"><span className="ci" style={{ background: 'var(--wa)' }}><MessageCircle className="i" /></span>Orçamento no WhatsApp</div>
            <div className="chip c2"><span className="ci" style={{ background: 'var(--orange)' }}><Wrench className="i" /></span>Ordem de serviço</div>
          </div>
        </div>
      </header>

      {/* ---------- Marquee de funcionalidades ---------- */}
      <div className="marquee" aria-hidden="true">
        <div className="marquee-track">
          {[0, 1].map((copy) => MARQUEE_ITEMS.map(({ Icon, title }) => (
            <span className="mq" key={`${copy}-${title}`}><Icon className="i" />{title}</span>
          )))}
        </div>
      </div>

      {/* ---------- Contadores ---------- */}
      <section className="stats">
        <div className="wrap">
          <div className="stats-grid reveal" ref={statsRef}>
            <Stat to={14} label="dias para testar grátis" play={statsPlay} />
            <Stat to={100} suffix="%" label="na nuvem, sem instalar nada" play={statsPlay} />
            <Stat to={FEATURE_GROUPS.length} label="áreas de gestão integradas" play={statsPlay} />
            <Stat to={TOTAL_FEATURES} label="funcionalidades no sistema" play={statsPlay} />
          </div>
        </div>
      </section>

      {/* ---------- Destaques ---------- */}
      <section className="highlights">
        <div className="wrap">
          <div className="hl-grid">
            {HIGHLIGHTS.map(({ Icon, title, text }, i) => (
              <div className="card-glow hl reveal" style={{ '--d': `${i * 0.1}s` }} key={title}>
                <div className="tile"><Icon className="i" /></div>
                <h3>{title}</h3>
                <p>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Telas do sistema ---------- */}
      <ScreensShowcase />

      {/* ---------- Funcionalidades ---------- */}
      <section className="features" id="funcionalidades">
        <div className="wrap">
          <div className="section-head reveal">
            <p className="eyebrow">Funcionalidades</p>
            <h2>Um módulo pra cada etapa do seu negócio</h2>
            <p>Do balcão ao relatório financeiro, cada área foi desenhada para o dia a dia de quem vende produtos e presta serviços.</p>
          </div>
          <div id="groups">
            {MAIN_GROUPS.map((group, i) => (
              <div className={`group${i % 2 ? ' alt' : ''}`} style={{ '--gc': group.color }} key={group.num}>
                <div className={`g-info reveal ${i % 2 ? 'right' : 'left'}`}>
                  <div className="g-num">{group.num}</div>
                  <h3>{group.title}</h3>
                  <div className="g-bar" />
                </div>
                <div className="g-cards">
                  {group.items.map(({ Icon, title, text }, k) => (
                    <div className="card-glow fc reveal" style={{ '--d': `${k * 0.1}s` }} key={title}>
                      <div className="ft"><Icon className="i" /></div>
                      <h4>{title}</h4>
                      <p>{text}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Inteligência e WhatsApp ---------- */}
      <section className="spot" id="ia">
        <div className="wrap">
          <div className="reveal left">
            <p className="eyebrow">{SPOT_GROUP.title}</p>
            <h2>Assistente de IA e WhatsApp direto do sistema</h2>
            <div className="spot-list">
              {SPOT_GROUP.items.map(({ Icon, title, text }) => (
                <div className="spot-item" key={title}>
                  <span className="ft"><Icon className="i" /></span>
                  <div><h4>{title}</h4><p>{text}</p></div>
                </div>
              ))}
            </div>
          </div>
          <div className="reveal right" ref={devicesRef} aria-hidden="true">
            <div className="devices">
              <div className="panel p1">
                <div className="p-head"><span className="ci"><Bot className="i" /></span>Assistente de IA<span className="ro">Somente consulta</span></div>
                <div className="ask"><span>{ask}</span><span className="caret" /></div>
                <div className="skel" style={{ width: '90%' }} />
                <div className="skel" style={{ width: '70%' }} />
                <div className="skel" style={{ width: '80%' }} />
              </div>
              <div className="panel p2">
                <div className="p-head"><span className="ci"><MessageCircle className="i" /></span>WhatsApp</div>
                <div className="bubble"><Wrench className="i" style={{ color: 'var(--orange)' }} />Ordem de serviço (PDF)</div>
                <div className="bubble"><Gem className="i" style={{ color: 'var(--violet)' }} />Orçamento</div>
                <div className="bubble"><BellRing className="i" style={{ color: 'var(--blue)' }} />Lembrete de próximo serviço</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- Planos (vindos do backend) ---------- */}
      <PlansSection />

      {/* ---------- CTA final / contato ---------- */}
      <section className="cta" id="contato">
        <div className="wrap">
          <div className="cta-box reveal">
            <h2>Pronto pra colocar sua operação em fluxo?</h2>
            <p>Comece grátis por 14 dias. Sem cartão de crédito, sem burocracia.</p>
            <div className="ctas">
              <Link to="/login?modo=cadastro" className="btn btn-white">Criar minha conta</Link>
              <a href={WHATSAPP_CONTACT_URL} target="_blank" rel="noopener noreferrer" className="btn btn-line">Falar com um consultor</a>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- Footer ---------- */}
      <footer>
        <div className="wrap">
          <div className="f-grid">
            <div>
              <Brand onClick={handleAnchor} />
              <p>
                Sistema de gestão para quem vende produtos e presta serviços — vendas, estoque, ordens de serviço e
                financeiro em um só lugar.
              </p>
            </div>
            <FooterCol title="Produto" links={[{ label: 'Funcionalidades', href: '#funcionalidades' }, { label: 'Planos', href: '#planos' }, { label: 'Atualizações' }]} onAnchor={handleAnchor} />
            <FooterCol title="Empresa" links={[{ label: 'Sobre' }, { label: 'Contato', href: '#contato' }, { label: 'Central de ajuda' }]} onAnchor={handleAnchor} />
            <FooterCol title="Legal" links={[{ label: 'Termos de uso', href: LEGAL_LINKS_ENABLED ? '/termos-de-uso' : undefined }, { label: 'Privacidade', href: LEGAL_LINKS_ENABLED ? '/politica-de-privacidade' : undefined }]} onAnchor={handleAnchor} />
          </div>
          <div className="f-bottom">© 2024 Flow Vendas. Todos os direitos reservados.</div>
        </div>
      </footer>
    </div>
  );
}

// Links sem destino (`href` ausente) são placeholders do protótipo (`href="#"`):
// as páginas "Sobre", "Termos" etc. ainda não existem. Ficam como texto neutro
// sem navegação, para não mandar o visitante ao topo sem motivo.
function FooterCol({ title, links, onAnchor }) {
  return (
    <div>
      <h5>{title}</h5>
      {links.map(({ label, href }) => (
        href
          ? <a key={label} href={href} onClick={onAnchor}>{label}</a>
          : <span key={label}>{label}</span>
      ))}
    </div>
  );
}
